import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Select from 'react-select'
import type { SingleValue } from 'react-select'
import type { LookupSelectProps, SelectOption } from './Select.types'
import {
  Label,
  buildClassNames,
  selectLayoutStyles,
  DropdownIndicator,
  ClearIndicator,
  IndicatorsContainer,
  RdControl,
  MenuListWithSearch,
  LoadingIndicator,
  fetchLookupPage,
} from './Select.utils'

export default function SelectLookup({
  name,
  lookupUrl,
  pageSize = 100,
  idField,
  initialValue,
  label,
  placeholder,
  required,
  disabled,
  floating,
  size = 'medium',
  error,
  helpText,
  clearable = true,
  autoWidth,
  menuPlacement = 'auto',
  creatable,
  onChange,
  setterRef,
}: LookupSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [value, setValue] = useState<SelectOption | null>(initialValue ?? null)

  useEffect(() => {
    if (!setterRef) return
    setterRef.current = (val: string, lbl?: string) => {
      setValue(val ? { value: val, label: lbl ?? val } : null)
    }
    return () => { setterRef.current = null }
  }, [setterRef])

  const [options, setOptions] = useState<SelectOption[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const pageRef = useRef(1)
  const timerRef = useRef<number | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const closeMenu = useCallback(() => {
    setIsOpen(false)
    setSearchQuery('')
    setOptions([])
  }, [])

  // Закрывать меню при клике вне компонента — react-select не видит клик снаружи
  // когда фокус находится на нашем кастомном инпуте поиска, а не на его скрытом инпуте.
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closeMenu()
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [isOpen, closeMenu])

  const load = useCallback(
    async (q: string, page: number) => {
      setIsLoading(true)
      try {
        const { options: next, hasMore: more } = await fetchLookupPage(lookupUrl, q, page, pageSize, idField)
        setOptions(prev => (page === 1 ? next : [...prev, ...next]))
        setHasMore(more)
      } finally {
        setIsLoading(false)
      }
    },
    [lookupUrl, pageSize, idField],
  )

  const handleSearchChange = useCallback(
    (q: string) => {
      setSearchQuery(q)
      if (timerRef.current !== null) clearTimeout(timerRef.current)
      timerRef.current = window.setTimeout(() => {
        pageRef.current = 1
        load(q, 1)
      }, 300)
    },
    [load],
  )

  const handleMenuOpen = useCallback(() => {
    setIsOpen(true)
    pageRef.current = 1
    load('', 1)
  }, [load])

  const handleSearchMouseDown = useCallback((e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation()
  }, [])

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') closeMenu()
  }, [closeMenu])

  const loadMore = useCallback(() => {
    if (isLoading) return
    pageRef.current += 1
    load(searchQuery, pageRef.current)
  }, [searchQuery, load, isLoading])

  const createValue = useMemo(() => {
    if (!creatable || !searchQuery.trim()) return null
    const trimmed = searchQuery.trim()
    if (options.some(o => o.label.toLowerCase() === trimmed.toLowerCase())) return null
    return trimmed
  }, [options, creatable, searchQuery])

  const displayOptions = useMemo(() => {
    if (!createValue) return options
    return [...options, { value: createValue, label: createValue }]
  }, [options, createValue])

  const formatOptionLabel = useCallback(
    (opt: SelectOption, { context }: { context: 'menu' | 'value' }) => {
      if (context === 'menu' && createValue && opt.value === createValue) {
        return (
          <span className="rd-dropdown__create-option">
            <span className="rd-dropdown__create-icon rd-icon-add" />
            {opt.label}
          </span>
        )
      }
      return opt.label
    },
    [createValue],
  )

  // Стабильная ссылка — react-select перемонтирует меню при каждом изменении объекта components
  const selectComponents = useMemo(() => ({
    DropdownIndicator, ClearIndicator, IndicatorsContainer, Control: RdControl,
    MenuList: MenuListWithSearch as any, LoadingIndicator,
  }), [])

  const handleChange = (opt: SingleValue<SelectOption>) => {
    setValue(opt ?? null)
    onChange?.(opt?.value ?? '', opt ?? null)
    closeMenu()
  }

  return (
    <>
      {label && !floating && <Label text={label} required={required} />}
      <div ref={containerRef} style={{ display: 'contents' }}>
        <Select<SelectOption>
          unstyled
          isSearchable={false}
          name={name}
          options={displayOptions}
          value={value}
          placeholder={floating ? '' : (placeholder ?? '')}
          isDisabled={disabled}
          isClearable={clearable}
          formatOptionLabel={creatable ? formatOptionLabel : undefined}
          filterOption={null}
          menuIsOpen={isOpen}
          menuPlacement={menuPlacement}
          minMenuHeight={300}
          onMenuOpen={handleMenuOpen}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          classNames={buildClassNames({ size, floating, error, isOpen, isFocused })}
          styles={selectLayoutStyles}
          components={selectComponents}
          floatingLabel={floating ? label : undefined}
          floatingRequired={floating ? required : undefined}
          sizerText={autoWidth ? (label || placeholder) : undefined}
          menuSearchQuery={searchQuery}
          onMenuSearchChange={handleSearchChange}
          onMenuSearchMouseDown={handleSearchMouseDown}
          onMenuSearchKeyDown={handleSearchKeyDown}
          menuHasMore={hasMore}
          onMenuLoadMore={loadMore}
          loadingMessage={() => null}
          noOptionsMessage={() => 'Нет результатов'}
        />
      </div>
      {error && <span className="rd-dropdown__error-text">{error}</span>}
      {helpText && <span className="rd-dropdown__helper-text">{helpText}</span>}
    </>
  )
}
